import { setUserId } from 'lib/analytics';
import { MeResponse } from 'components/login/types';
import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchUrlWithCookies } from '.';
import { signedOut } from './UserContext';
import { navigateTo } from 'lib/utils';

// From https://github.com/Modernizr/Modernizr/blob/master/feature-detects/storage/localstorage.js
// Via https://stackoverflow.com/a/16427747
function isLocalStorageAvailable() {
  const test = 'test';
  try {
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return true;
  } catch {
    return false;
  }
}

export function useMe(withTeamsV2: boolean = false) {
  const [me, setMe] = useState<MeResponse | undefined>(undefined);
  const [userLoaded, setUserLoaded] = useState<boolean>(false);
  const [cachedSignedOut, setCachedSignedOut] = useState<boolean>(false);
  const isMounted = useRef<boolean>(true);

  const fetchUser = useCallback(async () => {
    const url = withTeamsV2 ? '/api/me?withTeamsV2=true' : '/api/me';
    const res = await fetchUrlWithCookies(url);
    const user: MeResponse = await res.json();

    if (!isMounted.current) {
      return;
    }

    setMe(user);
    if (user.signedIn) {
      setUserId(user.ownerId ?? null);
    } else {
      setUserId(null);
    }
    setCachedSignedOut(false);
    setUserLoaded(true);
  }, [withTeamsV2]);

  function getCachedUserDetails() {
    if (typeof window === 'undefined' || !isLocalStorageAvailable()) {
      return;
    }

    const cachedSignedIn = localStorage.getItem('signedIn');
    if (cachedSignedIn === null) {
      return;
    }

    const signedIn = cachedSignedIn === '1';
    const avatar = localStorage.getItem('avatar');
    const username = localStorage.getItem('username');
    const name = localStorage.getItem('name');

    setMe({
      signedIn,
      avatar: avatar || undefined,
      username: username || undefined,
      name: name || undefined,
    });

    if (!signedIn) {
      setCachedSignedOut(true);
    }
  }

  function setCachedUserDetails(user: MeResponse) {
    if (!isLocalStorageAvailable()) {
      return;
    }

    if (user.signedIn) {
      localStorage.setItem('signedIn', '1');
      localStorage.setItem('avatar', user.avatar ?? '');
      localStorage.setItem('username', user.username ?? '');
      localStorage.setItem('name', user.name ?? '');
    } else {
      localStorage.setItem('signedIn', '0');
      localStorage.removeItem('avatar');
      localStorage.removeItem('username');
      localStorage.removeItem('name');
    }
  }

  async function logout() {
    const response = await fetchUrlWithCookies('/api/signout', {
      method: 'POST',
    });

    if (!response.ok) {
      return;
    }

    if (isLocalStorageAvailable()) {
      localStorage.setItem('signedIn', '0');
    }
    setMe(signedOut);
    // Invalidate user response.
    navigateTo('/login');
  }

  useEffect(() => {
    getCachedUserDetails();
    fetchUser();
    return () => {
      isMounted.current = false;
    };
  }, [fetchUser]);

  const isSignedIn = me?.signedIn;

  useEffect(() => {
    if (isSignedIn !== undefined) {
      setCachedUserDetails(me!);
    }
  }, [isSignedIn, me]);

  return {
    user: me,
    setUser: setMe,
    userLoaded,
    cachedSignedOut,
    reloadUser: fetchUser,
    logout,
  };
}
